import { type ReactNode } from 'react'
import styles from './ManageItems.module.css'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetContentBlock,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import { Form, FormGroup, FormRow, Help } from '@/components/Form'
import { IconButton } from '@/components/IconButton'
import cx from 'classnames'
import type { ManageItemsMode } from './ManageItemsViewModel'

export interface ManageItemsProps {
  showing: boolean
  mode: ManageItemsMode
  onModeChange: (mode: ManageItemsMode) => unknown
  onClose: () => unknown
  onSubmit: () => unknown
  children: ReactNode
}

export function ManageItems({
  showing,
  mode,
  onModeChange,
  onClose,
  onSubmit,
  children,
}: ManageItemsProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={onClose}
    >
      <ModalSheetHeader withCloseButton bordered>
        <div className={styles.modalSheetTitle}>Add more services</div>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <RadioButtonGroup label="What would you like to add?" size="big-wide">
            <RadioButtonItem
              onPress={() => onModeChange('AnExistingService')}
              active={mode === 'AnExistingService'}
              testId="job-scope-update-an-existing-service-button"
            >
              An existing service
            </RadioButtonItem>
            <RadioButtonItem
              onPress={() => onModeChange('NewCustomService')}
              active={mode === 'NewCustomService'}
              testId="job-scope-update-new-custom-service-button"
            >
              New custom service
            </RadioButtonItem>
          </RadioButtonGroup>
        </ModalSheetContentBlock>

        <ModalSheetContentBlock expanded>{children}</ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            testId="job-scope-update-manage-items-close-button"
          />
          <Button
            label="Add Services"
            variant="primary"
            onPress={onSubmit}
            testId="job-scope-update-manage-items-submit-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface ManageItemsExistingServiceProps {
  inventoryItemSelect: ReactNode
}

export function ManageItemsExistingService({
  inventoryItemSelect,
}: ManageItemsExistingServiceProps) {
  return (
    <Form>
      <FormRow>
        <FormGroup label="Service item">
          {inventoryItemSelect}
          <Help help="Select all service items that you would like to add" />
        </FormGroup>
      </FormRow>
    </Form>
  )
}

export interface ManageItemsInventoryItemOptionProps {
  name: string
  description: string
}

export function ManageItemsInventoryItemOption({
  name,
  description,
}: ManageItemsInventoryItemOptionProps) {
  return (
    <>
      <div className={styles.itemName}>{name}</div>
      <div className={styles.itemDescription}>{description}</div>
    </>
  )
}

export interface ManageItemsCustomServicesProps {
  hasItems: boolean
  onAddAnother: () => unknown
  children: ReactNode
}

export function ManageItemsCustomServices({
  hasItems,
  onAddAnother,
  children,
}: ManageItemsCustomServicesProps) {
  return (
    <Form>
      <FormRow>
        <FormGroup>
          {!hasItems && (
            <div className={styles.customItemsEmptyState}>
              <div className={styles.customItemsEmptyStateText}>
                There are no custom items on this job.
              </div>
            </div>
          )}

          {hasItems && (
            <div className={styles.newCustomServiceHeaderContainer}>
              <div className={styles.newCustomServiceHeader}>
                <span className={styles.customItemNameHeader}>Custom name</span>
              </div>
              <div className={styles.newCustomServiceHeader}>
                <span className={styles.customItemClientFeeHeader}>
                  Client fee
                </span>
                <span className={styles.customItemProCutHeader}>Pro cut</span>
              </div>
            </div>
          )}
          {children}

          <Button
            label="+ Add another"
            variant="text"
            onPress={onAddAnother}
            testId="job-scope-update-manage-items-add-another-button"
          />
        </FormGroup>
      </FormRow>
    </Form>
  )
}

export interface ManageCustomItemProps {
  name: string
  price: number
  proPrice: number
  nameError: string | undefined
  isFromJob: boolean
  onNameChange: (value: string) => unknown
  onPriceChange: (value: string) => unknown
  onProPriceChange: (value: string) => unknown
  onRemove: () => unknown
}

export function ManageCustomItem({
  name,
  price,
  proPrice,
  nameError,
  isFromJob,
  onNameChange,
  onPriceChange,
  onProPriceChange,
  onRemove,
}: ManageCustomItemProps) {
  return (
    <div className={styles.customItemContainer}>
      <div className={styles.customItemInputContainer}>
        <div className={styles.customItemNameInput}>
          <input
            type="text"
            className={cx(
              styles.input,
              styles.customItemName,
              nameError && styles.hasError,
            )}
            placeholder="Enter your service type"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={isFromJob}
            data-test={`job-scope-update-name-input-${name}`}
          />
          <Help error={nameError} />
        </div>
        <CustomItemMoneyInput
          value={price}
          field="price"
          onChange={onPriceChange}
          isDisabled={isFromJob}
          testId={`job-scope-update-price-input-${name}`}
        />
        <CustomItemMoneyInput
          value={proPrice}
          field="proPrice"
          onChange={onProPriceChange}
          isDisabled={isFromJob}
          testId={`job-scope-update-pro-price-input-${name}`}
        />
      </div>
      <IconButton
        name="close"
        label="Remove item"
        className={cx(
          styles.removeItemButton,
          nameError && styles.hasNameValidationError,
        )}
        onPress={onRemove}
        testId={`job-scope-update-manage-items-remove-button-${name}`}
        size={24}
      />
    </div>
  )
}

interface CustomItemMoneyInputProps {
  value: number
  field: 'price' | 'proPrice'
  onChange: (value: string) => unknown
  isDisabled?: boolean
  testId?: string
}

function CustomItemMoneyInput({
  value,
  field,
  onChange,
  isDisabled = false,
  testId,
}: CustomItemMoneyInputProps) {
  return (
    <div className={styles.customItemPriceInput}>
      <div className={styles.customItemPriceInputContainer}>
        <span className={styles.customItemPriceDollarSign}>$</span>
        <input
          type="number"
          disabled={isDisabled}
          className={cx(
            styles.customItemMoneyInput,
            field === 'price'
              ? styles.customItemClientFee
              : styles.customItemProCut,
          )}
          placeholder="0.00"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-test={testId}
        />
      </div>
    </div>
  )
}
